import { DealLensStatus, normalizeDealLensStatus } from "./dealsConstants";
import {
  resolveMessageThreadDisplayCopy,
  resolveMessageThreadViewerState,
} from "./dealOfferThreadUtils";
import { resolveOfferAmount } from "@/utils/maoCalculator/offerAmountUtils";
import { stripDeallensIdPrefix } from "./deallensIdUtils";

function formatOfferAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "$0";
  if (Math.abs(num) >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (Math.abs(num) >= 1_000) {
    return `$${Math.round(num / 1_000)}k`;
  }
  return `$${Math.round(num)}`;
}

function formatInsertDate(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} ${timePart}`;
}

function resolveOfferDirection(deal, viewerContext) {
  const { isReceiver, viewerKnown } = resolveMessageThreadViewerState(deal, viewerContext);
  if (!viewerKnown) return "unknown";
  return isReceiver ? "incoming" : "outgoing";
}

function formatFullOfferAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "";
  return `$${Math.round(num).toLocaleString("en-US")}`;
}

function buildOfferPrimaryLabel({
  offerAmount,
  hasPositiveAmount,
  hasMessage,
  isMessageDeal,
  direction,
  fallbackMessageLabel,
}) {
  if (isMessageDeal) {
    if (direction === "incoming") return "Message Received";
    if (direction === "outgoing") return "Message Sent";
    return fallbackMessageLabel;
  }

  const fullPrice = formatFullOfferAmount(offerAmount);
  if (!hasPositiveAmount) return fullPrice || "$0";

  if (!hasMessage || direction === "unknown") return fullPrice;
  const messageSuffix = direction === "incoming" ? "Message Received" : "Message Sent";
  return `${fullPrice} + ${messageSuffix}`;
}

function buildOfferAmountLabel({
  formattedAmount,
  hasPositiveAmount,
  hasMessage,
  isMessageDeal,
  direction,
  fallbackMessageLabel,
}) {
  if (isMessageDeal) {
    if (direction === "incoming") return "Message Received";
    if (direction === "outgoing") return "Message Sent";
    return fallbackMessageLabel;
  }

  if (!hasPositiveAmount) return formattedAmount;

  if (!hasMessage || direction === "unknown") return formattedAmount;
  const messageSuffix = direction === "incoming" ? "Message Received" : "Message Sent";
  return `${formattedAmount} + ${messageSuffix}`;
}

/**
 * @param {object} viewerContext
 * @param {{
 *   normalizedStatus: string,
 *   isMessageDeal: boolean,
 *   direction: string,
 *   isUnswiped: boolean,
 * }} flags
 * @returns {boolean}
 */
function resolveOfferIsNew(
  viewerContext,
  { normalizedStatus, isMessageDeal, direction, isUnswiped }
) {
  if (!isUnswiped) return false;

  if (viewerContext?.isInvestor) {
    if (isMessageDeal && direction === "incoming") return true;
    if (normalizedStatus === DealLensStatus.COUNTERED && direction === "incoming") return true;
    return false;
  }

  if (isMessageDeal && direction !== "unknown") {
    return direction === "incoming";
  }

  return true;
}

/**
 * Stable list-row id: collection offers share a deallens_id but differ by collection_id.
 *
 * @param {object | null | undefined} deal
 * @returns {string | null}
 */
export function resolvePropertyOfferListId(deal) {
  const collectionId = String(deal?.collection_id ?? "").trim();
  if (collectionId) return collectionId;
  const deallensId = String(deal?.deallens_id ?? deal?.id ?? "").trim();
  return deallensId || null;
}

/**
 * Legacy rows without collection_id still advance in place via deallens lineage.
 *
 * @param {object} row
 * @param {object} incoming
 * @returns {boolean}
 */
function legacyPropertyOfferLineageMatch(row, incoming) {
  const rowToken = resolveDealDeallensToken(row);
  const incomingToken = resolveDealDeallensToken(incoming);
  if (rowToken && incomingToken && rowToken === incomingToken) return true;

  const rowPrevToken = stripDeallensIdPrefix(row.metadata?.previous_deallens_id ?? "");
  if (incomingToken && rowPrevToken && incomingToken === rowPrevToken) return true;

  const incomingPrevToken = stripDeallensIdPrefix(incoming.metadata?.previous_deallens_id ?? "");
  if (incomingPrevToken && rowToken && incomingPrevToken === rowToken) return true;

  if (
    incomingPrevToken &&
    rowPrevToken &&
    incomingPrevToken === rowPrevToken &&
    isOptimisticDealId(incoming) !== isOptimisticDealId(row)
  ) {
    return true;
  }

  return false;
}

/**
 * Whether an update targets the same property-offer list row.
 * Prefer collection_id equality when both sides have it; if that fails (or only one
 * side has collection_id — e.g. API lag), fall back to deallens lineage matching.
 *
 * @param {object | null | undefined} row
 * @param {object | null | undefined} incoming
 * @returns {boolean}
 */
export function propertyOfferRowsMatch(row, incoming) {
  if (!row || !incoming) return false;

  const rowListId = resolvePropertyOfferListId(row);
  const incomingListId = resolvePropertyOfferListId(incoming);
  const rowHasCollection = Boolean(String(row?.collection_id ?? "").trim());
  const incomingHasCollection = Boolean(String(incoming?.collection_id ?? "").trim());

  if (rowHasCollection && incomingHasCollection) {
    return Boolean(rowListId && incomingListId && rowListId === incomingListId);
  }

  if (rowListId && incomingListId && rowListId === incomingListId) return true;

  return legacyPropertyOfferLineageMatch(row, incoming);
}

/**
 * History state must only contain structuredClone-safe primitives (browser pushState).
 *
 * @param {object | null | undefined} state
 * @returns {object}
 */
export function cloneSafePropertyOfferNavigationState(state) {
  if (!state || typeof state !== "object") return {};
  const { returnTo, propertyOfferBootstrapKey, propertyOfferBootstrapPropertyId } = state;
  const next = {};
  const safeReturnTo = String(returnTo ?? "").trim();
  const safeKey = String(propertyOfferBootstrapKey ?? "").trim();
  const safePropertyId = String(propertyOfferBootstrapPropertyId ?? "")
    .trim()
    .replace(/^property#/i, "");
  if (safeReturnTo) next.returnTo = safeReturnTo;
  if (safeKey) next.propertyOfferBootstrapKey = safeKey;
  if (safePropertyId) next.propertyOfferBootstrapPropertyId = safePropertyId;
  return next;
}

/**
 * @param {object} [params]
 * @param {string | null | undefined} [params.returnTo]
 * @param {string | null | undefined} [params.propertyId]
 * @param {object | null | undefined} [params.offer]
 * @param {object | null | undefined} [params.baseState]
 * @returns {object}
 */
export function buildPropertyOfferNavigationState({
  returnTo,
  propertyId,
  offer,
  baseState = null,
} = {}) {
  const next = cloneSafePropertyOfferNavigationState(baseState);
  const safeReturnTo = String(returnTo ?? "").trim();
  const listId = offer ? resolvePropertyOfferListId(offer?._raw ?? offer) : "";
  const normPropertyId = String(propertyId ?? "")
    .trim()
    .replace(/^property#/i, "");
  if (safeReturnTo) next.returnTo = safeReturnTo;
  if (listId) next.propertyOfferBootstrapKey = listId;
  if (normPropertyId) next.propertyOfferBootstrapPropertyId = normPropertyId;
  return next;
}

/**
 * Resolves the clicked property-offer row from navigation keys + in-memory cache.
 *
 * @param {object | null | undefined} deallensStore
 * @param {object} params
 * @param {string | null | undefined} params.customer
 * @param {string | null | undefined} params.propertyId
 * @param {object | null | undefined} params.locationState
 * @returns {object | null}
 */
export function resolvePropertyOfferBootstrapFromLocation(
  deallensStore,
  { customer, propertyId, locationState }
) {
  const key = String(locationState?.propertyOfferBootstrapKey ?? "").trim();
  const propId =
    String(locationState?.propertyOfferBootstrapPropertyId ?? propertyId ?? "")
      .trim()
      .replace(/^property#/i, "") || null;
  if (key && customer && propId && typeof deallensStore?.findRawPropertyOfferDeal === "function") {
    const cached = deallensStore.findRawPropertyOfferDeal(customer, propId, key);
    if (cached) return cached;
  }
  return null;
}

/**
 * Stable list-row id: collection offers share a deallens_id but differ by collection_id.
 *
 * @param {object | null | undefined} dealOrOffer
 * @returns {string}
 */
export function resolvePropertyOfferDeallensId(dealOrOffer) {
  const raw = dealOrOffer?._raw ?? dealOrOffer;
  return String(raw?.deallens_id ?? raw?.deallensId ?? raw?.id ?? "").trim();
}

/**
 * @param {object | null | undefined} deal
 * @returns {string}
 */
export function resolveDealDeallensToken(deal) {
  return stripDeallensIdPrefix(resolvePropertyOfferDeallensId(deal));
}

/**
 * @param {object | null | undefined} deal
 * @returns {boolean}
 */
export function isOptimisticDealId(deal) {
  return resolveDealDeallensToken(deal).startsWith("optimistic:");
}

/**
 * @param {object[]} deals
 * @returns {object[]}
 */
export function dedupePropertyOfferDeals(deals) {
  if (!Array.isArray(deals)) return [];
  const seen = new Set();
  const deduped = [];
  for (const deal of deals) {
    const key = resolvePropertyOfferListId(deal) ?? "";
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(deal);
  }
  return deduped;
}

/**
 * @param {object | null | undefined} deal
 * @returns {boolean}
 */
function isDraftOpenShare(deal) {
  if (normalizeDealLensStatus(deal?.status) !== DealLensStatus.OPEN) return false;
  const explicitAmount = Number(deal?.calculation?.offer_amount);
  return !(Number.isFinite(explicitAmount) && explicitAmount > 0);
}

/**
 * @param {object[]} deals
 * @returns {object[]}
 */
export function collapsePropertyOfferLineages(deals) {
  if (!Array.isArray(deals) || deals.length === 0) return [];

  const byToken = new Map();
  for (const deal of deals) {
    const token = resolveDealDeallensToken(deal);
    if (token) byToken.set(token, deal);
  }

  const supersededTokens = new Set();
  for (const deal of deals) {
    const dealToken = resolveDealDeallensToken(deal);
    const previousToken = stripDeallensIdPrefix(deal?.metadata?.previous_deallens_id ?? "");
    if (!previousToken) continue;
    // Offer revisions can reuse the same DealLens id; a self-reference must not remove the row.
    if (dealToken && previousToken === dealToken) continue;

    const predecessor = byToken.get(previousToken);
    if (!predecessor) continue;

    const predecessorCollection = String(predecessor?.collection_id ?? "").trim();
    const successorCollection = String(deal?.collection_id ?? "").trim();

    // Draft OPEN shares are never real offer rows — hide them once any successor exists,
    // even when "Make a new Offer" assigned a fresh collection_id.
    if (isDraftOpenShare(predecessor)) {
      supersededTokens.add(previousToken);
      continue;
    }

    if (predecessorCollection === successorCollection) {
      supersededTokens.add(previousToken);
      continue;
    }

    if (!predecessorCollection && !successorCollection) {
      supersededTokens.add(previousToken);
    }
  }

  return deals.filter((deal) => {
    const token = resolveDealDeallensToken(deal);
    return !token || !supersededTokens.has(token);
  });
}

/**
 * @param {object} deal
 * @param {number} index
 * @param {object} [viewerContext]
 * @returns {object}
 */
export function transformDealToOffer(deal, index, viewerContext = {}) {
  const normalizedStatus = normalizeDealLensStatus(deal?.status);
  const offerAmount = resolveOfferAmount(deal?.calculation);
  const formattedDate = formatInsertDate(deal?.insert_date);
  const isMessageDeal = normalizedStatus === DealLensStatus.MESSAGE;
  const isUnswiped = deal.swipe_status === "UNSWIPED";
  const statusLabel = normalizedStatus === DealLensStatus.WITHDRAWN ? "Withdrawn" : "";
  const messageText =
    typeof deal?.metadata?.message === "string" ? deal.metadata.message.trim() : "";
  const hasPositiveAmount = Number.isFinite(Number(offerAmount)) && Number(offerAmount) > 0;
  const formattedAmount = formatOfferAmount(offerAmount);
  const direction = resolveOfferDirection(deal, viewerContext);
  const { label: fallbackMessageLabel, datePrefix: messageDatePrefix } =
    resolveMessageThreadDisplayCopy(deal, viewerContext);
  const datePrefix = isMessageDeal
    ? messageDatePrefix
    : direction === "outgoing"
      ? "Sent on"
      : "Received on";

  const amount = buildOfferAmountLabel({
    formattedAmount,
    hasPositiveAmount,
    hasMessage: Boolean(messageText),
    isMessageDeal,
    direction,
    fallbackMessageLabel,
  });
  const primaryLabel = buildOfferPrimaryLabel({
    offerAmount,
    hasPositiveAmount,
    hasMessage: Boolean(messageText),
    isMessageDeal,
    direction,
    fallbackMessageLabel,
  });
  const time = formattedDate ? `${datePrefix} ${formattedDate}` : "";
  const isNew = resolveOfferIsNew(viewerContext, {
    normalizedStatus,
    isMessageDeal,
    direction,
    isUnswiped,
  });

  return {
    id: resolvePropertyOfferListId(deal) ?? deal.id,
    deallensId: resolvePropertyOfferDeallensId(deal),
    number: index + 1,
    isNew,
    time,
    amount,
    primaryLabel,
    direction,
    sender: deal.sender ?? null,
    receiver: deal.receiver ?? null,
    statusLabel,
    _offerAmount: offerAmount,
    _insertDate: deal?.insert_date ?? "",
    _raw: deal,
  };
}

/**
 * @param {object[]} rawDeals
 * @param {object} [viewerContext]
 * @param {Set<string>} [readOfferIds]
 * @returns {object[]}
 */
export function transformPropertyOfferRows(rawDeals, viewerContext = {}, readOfferIds = null) {
  const readIdsSet = readOfferIds instanceof Set ? readOfferIds : new Set();
  return dedupePropertyOfferDeals(collapsePropertyOfferLineages(rawDeals)).map((deal, index) => {
    const offer = transformDealToOffer(deal, index, viewerContext);
    if (offer.isNew && readIdsSet.has(String(offer.id))) {
      return { ...offer, isNew: false };
    }
    return offer;
  });
}
